import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAgeGroups, deleteAgeGroup, checkUserCanManageAgeGroups } from '../../services/ageGroupService';
import { getParticipants } from '../../services/participantService';
import { getCompetitions } from '../../services/competitionService';
import { AgeGroup, Participant, Competition } from '../../types/models';
import { Plus, Calendar, Search, Download, FileSpreadsheet, FileText, File as FilePdf, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { exportToExcel, exportToCSV, exportToPDF } from '../../utils/export';
import { useAuthStore } from '../../store/authStore';
import { supabase } from '../../lib/supabase';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import pdfMake from 'pdfmake/build/pdfmake';
import { vfs } from '../../fonts/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';

const ITEMS_PER_PAGE = 10;

const AgeGroupList: React.FC = () => {
  const [ageGroups, setAgeGroups] = useState<AgeGroup[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<AgeGroup | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showCompetitionsExportMenu, setShowCompetitionsExportMenu] = useState(false);
  const [canManageAgeGroups, setCanManageAgeGroups] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const { t } = useTranslation('common');
  const { user } = useAuthStore();

  useEffect(() => {
    const checkUserRoles = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }

      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', user.id);

      setIsAdmin(userRoles?.some(ur => ur.roles.role_name === 'admin') ?? false);
    };

    checkUserRoles();
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [ageGroupsData, participantsData, competitionsData, canManage] = await Promise.all([
          getAgeGroups(),
          getParticipants(),
          getCompetitions(),
          checkUserCanManageAgeGroups()
        ]);
        setAgeGroups(ageGroupsData);
        setParticipants(participantsData);
        setCompetitions(competitionsData);
        setCanManageAgeGroups(canManage);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this age group?')) {
      return;
    }

    try {
      setError(null);
      await deleteAgeGroup(id);
      setAgeGroups(prev => prev.filter(group => group.age_group_id !== id));
    } catch (err: any) {
      console.error('Error deleting age group:', err);
      setError(err.message || 'Failed to delete age group');
    }
  };

  const getAgeGroupParticipants = (birthYears: number[]) => {
    return participants.filter(p => {
      const birthYear = new Date(p.date_of_birth).getFullYear();
      return birthYears.includes(birthYear);
    });
  };

  const getAgeGroupCompetitions = (ageGroupId: string) => {
    return competitions.filter(c => c.age_group_id === ageGroupId);
  };

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    if (!selectedAgeGroup) return;
    
    const ageGroupParticipants = getAgeGroupParticipants(selectedAgeGroup.birth_years);
    switch (format) {
      case 'excel':
        exportToExcel(ageGroupParticipants);
        break;
      case 'csv':
        exportToCSV(ageGroupParticipants);
        break;
      case 'pdf':
        exportToPDF(ageGroupParticipants);
        break;
    }
    setShowExportMenu(false);
  };

  const handleExportCompetitions = (format: 'excel' | 'pdf') => {
    if (!selectedAgeGroup) return;
    
    const ageGroupCompetitions = getAgeGroupCompetitions(selectedAgeGroup.age_group_id);
    
    if (ageGroupCompetitions.length === 0) {
      alert('No competitions found for this age group');
      return;
    }
    
    switch (format) {
      case 'excel':
        exportCompetitionsToExcel(ageGroupCompetitions, selectedAgeGroup.group_name);
        break;
      case 'pdf':
        exportCompetitionsToPDF(ageGroupCompetitions, selectedAgeGroup.group_name);
        break;
    }
    setShowCompetitionsExportMenu(false);
  };

  const exportCompetitionsToExcel = (competitions: Competition[], ageGroupName: string) => {
    // Format data for Excel
    const data = competitions.map(comp => ({
      'Event Name': comp.event_name,
      'Reference Number': comp.reference_number,
      'Verification Code': comp.verification_code || '',
      'Category': comp.category?.category_name || '',
      'Age Group': ageGroupName,
      'Gender': comp.gender_category === 'M' ? 'Male' : 
                comp.gender_category === 'F' ? 'Female' : 'All',
      'Status': comp.status === 'not_started' ? 'Not Started' :
                comp.status === 'in_progress' ? 'In Progress' : 'Completed',
      'Team Event': comp.is_team_event ? 'Yes' : 'No'
    }));

    // Create workbook
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, ageGroupName);
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `competitions_${ageGroupName.replace(/\s+/g, '_')}.xlsx`);
  };

  const exportCompetitionsToPDF = async (competitions: Competition[], ageGroupName: string) => {
    // Initialize pdfmake with virtual font files
    pdfMake.vfs = vfs;

    // Configure fonts for pdfmake
    pdfMake.fonts = {
      NotoSansTamil: {
        normal: 'NotoSansTamil-Regular.ttf',
        bold: 'NotoSansTamil-Bold.ttf',
        italics: 'NotoSansTamil-Regular.ttf',
        bolditalics: 'NotoSansTamil-Bold.ttf'
      },
      Roboto: {
        normal: 'Roboto-Regular.ttf',
        bold: 'Roboto-Bold.ttf',
        italics: 'Roboto-Regular.ttf',
        bolditalics: 'Roboto-Bold.ttf'
      }
    };

    // Get school logo if available
    let logoUrl = null;
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_name', 'school_logo')
        .single();
      
      if (!error && data?.setting_value?.url) {
        logoUrl = data.setting_value.url;
      }
    } catch (error) {
      console.error('Error fetching school logo:', error);
    }

    let logoImage = null;
    if (logoUrl) {
      try {
        // Fetch the image and convert to base64
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        const reader = new FileReader();
        logoImage = await new Promise((resolve) => {
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error loading logo image:', error);
      }
    }

    // Create PDF content
    const docDefinition: TDocumentDefinitions = {
      pageOrientation: 'landscape',
      footer: function(currentPage, pageCount) {
        return {
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: 'right',
          margin: [0, 0, 40, 0]
        };
      },
      content: [
        {
          columns: [
            // Left column for logo
            {
              width: 100,
              stack: logoImage ? [
                {
                  image: logoImage as string,
                  width: 80,
                  alignment: 'left'
                }
              ] : [],
              margin: [0, 0, 10, 0]
            },
            // Right column for title
            {
              width: '*',
              text: `Competitions - ${ageGroupName}`,
              style: 'header',
              alignment: 'center'
            },
            // Empty column for balance
            {
              width: 100,
              text: ''
            }
          ]
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto', 'auto', '*', 'auto', 'auto', 'auto', 'auto'],
            body: [
              [
                { text: 'Event Name', style: 'tableHeader' },
                { text: 'Reference Number', style: 'tableHeader' },
                { text: 'Verification Code', style: 'tableHeader' },
                { text: 'Category', style: 'tableHeader' },
                { text: 'Age Group', style: 'tableHeader' },
                { text: 'Gender', style: 'tableHeader' },
                { text: 'Status', style: 'tableHeader' },
                { text: 'Team Event', style: 'tableHeader' }
              ],
              ...competitions.map(comp => [
                comp.event_name,
                comp.reference_number,
                comp.verification_code || '',
                comp.category?.category_name || '',
                ageGroupName,
                comp.gender_category === 'M' ? 'Male' : 
                comp.gender_category === 'F' ? 'Female' : 'All',
                comp.status === 'not_started' ? 'Not Started' :
                comp.status === 'in_progress' ? 'In Progress' : 'Completed',
                comp.is_team_event ? 'Yes' : 'No'
              ])
            ]
          },
          margin: [0, 20, 0, 0]
        }
      ],
      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 20]
        },
        tableHeader: {
          bold: true,
          fontSize: 12,
          color: 'black'
        }
      },
      defaultStyle: {
        font: 'Roboto'
      }
    };

    // Generate and download the PDF
    pdfMake.createPdf(docDefinition).download(`competitions_${ageGroupName.replace(/\s+/g, '_')}.pdf`);
  };

  const filteredAgeGroups = ageGroups.filter(group =>
    group.group_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    group.birth_years.some(year => year.toString().includes(searchTerm))
  );

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(filteredAgeGroups.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedAgeGroups = filteredAgeGroups.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('ageGroups.title')}</h1>
        {user && canManageAgeGroups && (
          <Link
            to="/age-groups/new"
            className="mt-3 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('ageGroups.actions.addAgeGroup')}
          </Link>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('ageGroups.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredAgeGroups.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('ageGroups.noAgeGroups.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? t('ageGroups.noAgeGroups.description.withFilters') : t('ageGroups.noAgeGroups.description.noFilters')}
            </p>
            {user && canManageAgeGroups && (
              <div className="mt-6">
                <Link
                  to="/age-groups/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('ageGroups.actions.addAgeGroup')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('ageGroups.table.groupName')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('ageGroups.table.birthYears')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('ageGroups.table.students')}
                    </th>
                    {user && canManageAgeGroups && (
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">{t('ageGroups.table.actions')}</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedAgeGroups.map((group) => {
                    const groupParticipants = getAgeGroupParticipants(group.birth_years);
                    const groupCompetitions = getAgeGroupCompetitions(group.age_group_id);
                    return (
                      <tr key={group.age_group_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => {
                              setSelectedAgeGroup(selectedAgeGroup?.age_group_id === group.age_group_id ? null : group);
                            }}
                            className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                          >
                            {group.group_name}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{group.birth_years.join(', ')}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {groupParticipants.length} {t('ageGroups.table.students')}
                          </div>
                        </td>
                        {user && canManageAgeGroups && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-3">
                              <Link
                                to={`/age-groups/${group.age_group_id}/edit`}
                                className="text-indigo-600 hover:text-indigo-800"
                              >
                                {t('ageGroups.actions.edit')}
                              </Link>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDelete(group.age_group_id)}
                                  className="text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(startIndex + ITEMS_PER_PAGE, filteredAgeGroups.length)}
                    </span>{' '}
                    of <span className="font-medium">{filteredAgeGroups.length}</span> results
                  </p>
                </div>
                {totalPages > 1 && (
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            page === currentPage
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                )}
              </div>
            </div>

            {selectedAgeGroup && (
              <div className="mt-8 px-6 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {selectedAgeGroup.group_name} {t('ageGroups.table.students')}
                  </h2>
                  <div className="flex space-x-3">
                    <div className="relative">
                      <button
                        onClick={() => setShowExportMenu(!showExportMenu)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {t('ageGroups.actions.export')} {t('participants.title')}
                      </button>
                      
                      {showExportMenu && (
                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                          <div className="py-1" role="menu">
                            <button
                              onClick={() => handleExport('excel')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <FileSpreadsheet className="h-4 w-4 mr-2" />
                              {t('participants.export.toExcel')}
                            </button>
                            <button
                              onClick={() => handleExport('csv')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              {t('participants.export.toCSV')}
                            </button>
                            <button
                              onClick={() => handleExport('pdf')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <FilePdf className="h-4 w-4 mr-2" />
                              {t('participants.export.toPDF')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <button
                        onClick={() => setShowCompetitionsExportMenu(!showCompetitionsExportMenu)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {t('ageGroups.actions.export')} {t('competitions.title')}
                      </button>
                      
                      {showCompetitionsExportMenu && (
                        <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                          <div className="py-1" role="menu">
                            <button
                              onClick={() => handleExportCompetitions('excel')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <FileSpreadsheet className="h-4 w-4 mr-2" />
                              {t('participants.export.toExcel')}
                            </button>
                            <button
                              onClick={() => handleExportCompetitions('pdf')}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                            >
                              <FilePdf className="h-4 w-4 mr-2" />
                              {t('participants.export.toPDF')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Competitions for this age group */}
                {getAgeGroupCompetitions(selectedAgeGroup.age_group_id).length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      {t('competitions.title')} - {selectedAgeGroup.group_name}
                    </h3>
                    <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('competitions.table.eventName')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('competitions.table.referenceNumber')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('competitions.table.verificationCode')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('competitions.table.category')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('competitions.table.gender')}
                            </th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {t('competitions.table.status')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {getAgeGroupCompetitions(selectedAgeGroup.age_group_id).map((competition) => (
                            <tr key={competition.event_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Link to={`/competitions/${competition.event_id}`} className="text-blue-600 hover:text-blue-900">
                                  {competition.event_name}
                                </Link>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-mono text-gray-900">
                                  {competition.reference_number}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-mono text-gray-900">
                                  {competition.verification_code}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">
                                  {competition.category?.category_name}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  competition.gender_category === 'M' ? 'bg-blue-100 text-blue-800' :
                                  competition.gender_category === 'F' ? 'bg-pink-100 text-pink-800' :
                                  'bg-purple-100 text-purple-800'
                                }`}>
                                  {competition.gender_category === 'M' ? t('competitions.filters.male') :
                                   competition.gender_category === 'F' ? t('competitions.filters.female') : 
                                   t('competitions.filters.all')}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  competition.status === 'not_started' ? 'bg-gray-100 text-gray-800' :
                                  competition.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {t(`competitions.status.${competition.status}`)}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Participants for this age group */}
                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.name')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.tamilName')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.dateOfBirth')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.gender')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.house')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getAgeGroupParticipants(selectedAgeGroup.birth_years).map((participant) => (
                        <tr key={participant.student_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link to={`/participants/${participant.student_id}`}>
                              <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                                {participant.first_name} {participant.last_name}
                              </div>
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {participant.first_name_tamil} {participant.last_name_tamil}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {format(new Date(participant.date_of_birth), 'dd.MM.yyyy')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              participant.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                            }`}>
                              {participant.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div 
                                className="h-3 w-3 rounded-full mr-2" 
                                style={{ backgroundColor: participant.house?.house_color }}
                              />
                              <div className="text-sm text-gray-900">
                                {participant.house?.house_name}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AgeGroupList;